import { controlServiceImpl } from "@lvt/streaming";
import {
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Input,
    Grid,
    makeStyles,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import { Skeleton } from "@material-ui/lab";
import { useState } from "react";

import client from "@/api/client";
import { useUpdatePresetMutation } from "@/api/liveUnitMosaic/liveUnitMosaic";
import useActionButtonStyles from "@/common/components/streaming/ActionButtons/ActionButton.styles";
import * as FullStory from "@/utilities/hooks/fullstory";
import { OpenButton } from "@/utilities/types/streaming";

import { Preset } from "./PresetsButton";


const useStyles = makeStyles((theme) => ({
    listItem: {
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
        },
    },
    inputText: {
        color: theme.palette.primary.contrastText + " !important",
    },
    editIcon: {
        fill: theme.palette.primary.contrastText,
    },
}));

interface PresetsMenuItemProps {
    allowSelfService?: boolean;
    handleMenu: (button: OpenButton) => void;
    liveUnitId: number;
    preset: Preset;
    showEdit: boolean;
    variant?: "mosaic" | "default";
    cameraId: number | null
}

const PresetsMenuItem: React.FC<PresetsMenuItemProps> = ({
    allowSelfService,
    handleMenu,
    liveUnitId,
    preset,
    showEdit,
    variant = "default",
    cameraId,
}) => {
    const actionButtonClasses = useActionButtonStyles();
    const styles = useStyles();

    const [editing, setEditing] = useState<null | string>(null);
    const [saving, setSaving] = useState(false);

    const [updatePreset] = useUpdatePresetMutation();

    const controlService = controlServiceImpl(client, cameraId!); // cameraId should never be null

    const handleEdit = () => {
        setEditing(preset.name);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (editing) {
            setSaving(true);

            updatePreset({
                liveUnitId,
                presetId: preset.id,
                name: editing,
            }).then(() => {
                setSaving(false);
                setEditing(null);
            });
        }
    };

    const handleClick = async () => {
        if (cameraId) {
            await controlService.goToPreset(preset.id);

            handleMenu(OpenButton.None);
        }
    };

    return (
        <ListItem key={preset.id} button classes={{ root: styles.listItem }} className={FullStory.names.classes.streaming.preset}>
            {editing !== null ? (
                <form id={`${preset.name}-form`} onSubmit={handleSubmit}>
                    <Input
                        id={`edit-${preset.name}`}
                        inputProps={{
                            "aria-label": `${preset.name.toLowerCase()}-input`,
                        }}
                        classes={{
                            root: styles.inputText,
                        }}
                        value={editing}
                        onChange={(e) => setEditing(e.target.value)}
                        autoFocus
                        required
                    />
                </form>
            ) : (
                <ListItemText
                    primary={preset.name}
                    onClick={handleClick}
                    classes={{ primary: variant === "mosaic" ? actionButtonClasses.mosaicListItem : undefined }}
                />
            )}

            {showEdit &&
                <ListItemSecondaryAction>
                    {editing !== null ? (
                        <Grid container>
                            <Grid item xs={6}>
                                <IconButton aria-label="cancel-edit" onClick={() => setEditing(null)}>
                                    <CloseIcon color="error" />
                                </IconButton>
                            </Grid>
                            <Grid item xs={6}>
                                <IconButton aria-label="save-preset" type="submit" form={`${preset.name}-form`}>
                                    <CheckIcon color="secondary" />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ) : saving ? (
                        <Skeleton variant="circle">
                            <IconButton edge="end">
                                <EditIcon />
                            </IconButton>
                        </Skeleton>
                    ) : allowSelfService ? (
                        <IconButton edge="end" aria-label={`edit-${preset.name.toLowerCase()}`} onClick={handleEdit}>
                            <EditIcon classes={{ root: styles.editIcon }} />
                        </IconButton>
                    ) : (<></>)}
                </ListItemSecondaryAction>
            }
        </ListItem>
    );
};
export default PresetsMenuItem;
